/**
 * Device ID — a persistent per-browser UUID used to key the anonymous
 * rate limiter on the chat endpoint. Generated on first read, kept in
 * localStorage, sent as `X-Device-Id` on `/api/chat/weather` requests.
 *
 * The backend hashes this together with the client IP (sha256 prefix),
 * so leaking the ID does nothing on its own and a single IP can't
 * multiply-limit by rotating IDs — the IP anchors the bucket.
 */
const KEY = 'wital_device_id'

function generateUuid(): string {
  // crypto.randomUUID is widely available (Safari 15.4+, Chrome 92+,
  // Firefox 95+). Fallback only on ancient browsers — those users won't
  // exist on wital.ai but let's not crash.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(KEY)
    if (existing && existing.length >= 8) return existing
    const fresh = generateUuid()
    localStorage.setItem(KEY, fresh)
    return fresh
  } catch {
    // localStorage blocked (private browsing on some platforms) — fall
    // through to a per-session in-memory value so we at least stay
    // within one user between reloads in the same session.
    return generateUuid()
  }
}
